import React, { useState } from "react";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import apiClient from "../../axiosClient";
// Custom types and defs
import callerDidColDef from "./colDef/callerDidConfig";
import { apiCallWrapper } from "../../utils/commonUtils";
import ActionConfirmationModal from "../modals/actionConfirmationModal";
import CallerDID from "./interface/callerDid";
import AddCallerDIDModal from "./modal/addCallerDids";
import UpdateCallerDIDModal from "./modal/updateCallerDid";

interface CallerDIDConfigComponentProp {
	loading: boolean,
	callerDIDs: CallerDID[],
	refreshCallerDIDs: () => void
}

export interface CallerDidUpdateRequest {
	agency_name: string|undefined,
	client_name: string|undefined,
}

const CallerDIDConfig: React.FC<CallerDIDConfigComponentProp> = ({ loading, callerDIDs, refreshCallerDIDs}) => {
	const [selectedRows, setSelectedRows] = useState<CallerDID[]>([]);

	const [updateCallerDIDModal, setUpdateCallerDIDModalVisibility] = useState<boolean>(false);
	const [openDeactivateAction, setDeactivateActionVisibility] = useState<boolean>(false);
	const [openCallerDIDModal, setAddCallerDIDModalVisibility] = useState<boolean>(false)

	const addNewCallerDIDs = async (newCallerDids: string[]) => {
		let payload: any = [];
		newCallerDids.forEach((callerDid) => {
			payload.push({
				caller_did: callerDid,
			});
		});
		const response = await apiCallWrapper(() => apiClient.post(`/api/v1/tcn_config/caller_did_agency_mapping`, payload));
		if (response && (response.status === 200 || response.status === 201)) {
			alert("Caller DIDs added successfully");
			setAddCallerDIDModalVisibility(false);
			refreshCallerDIDs();
		}
	};

	const deactivateCallerDids = async () => {
		if (selectedRows.length > 0) {
			let ids = selectedRows.map((selectedRow) => selectedRow.id);
			const response = await apiCallWrapper(() => apiClient.post(`/api/v1/tcn_config/caller_did_agency_mapping/bulk-delete`, ids));
			if (response && (response.status === 200 || response.status === 201)) {
				alert("Deactivation successfull");
				setDeactivateActionVisibility(false);
				refreshCallerDIDs();
			}
		} else {
			alert("Select caller dids to deactivate");
		}
	}

	const updateCallerDid = async (id: string, update_request: CallerDidUpdateRequest) => {
		const response = await apiCallWrapper(() => apiClient.put(`/api/v1/tcn_config/caller_did_agency_mapping/${id}`, update_request));
		if (response && (response.status === 200 || response.status === 201)) {
			alert("Update successfull");
			setUpdateCallerDIDModalVisibility(false);
			refreshCallerDIDs();
		}
	}

	const handleSelectRow = (ids: GridRowSelectionModel) => {
		setSelectedRows(
			callerDIDs.filter((callerDid: CallerDID) => Array.from(ids.ids).includes(callerDid.id))
		);
	};

	return (
		<Box sx={{ margin: '10px', display: 'flex', flexDirection: 'column' }}>

			<Stack
				direction="row"
				spacing={2}
				justifyContent="flex-end"
				sx={{ mb: 2 }}
			>
				{selectedRows.length === 1 && (
					<Button variant="contained" onClick={() => setUpdateCallerDIDModalVisibility(true)}>Update Caller DID</Button>
				)}
				{selectedRows.length > 0 && (
					<Button variant="contained" onClick={() => setDeactivateActionVisibility(true)}>Deactivate Caller DID</Button>
				)}
				<Button variant="contained" onClick={() => setAddCallerDIDModalVisibility(true)}>Add Caller DID</Button>
			</Stack>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
					<CircularProgress />
				</Box>
			) : (
				<SearchResultGrid
					columns={callerDidColDef}
					results={callerDIDs}
					rowCount={callerDIDs.length}
					onSelectRow={handleSelectRow}
					getRowId={(row: CallerDID) => row.id}
					disableFilter={false}
					disableSorting={false}
				/>
			)}

			{updateCallerDIDModal && <UpdateCallerDIDModal 
				open={updateCallerDIDModal} 
				onClose={() => setUpdateCallerDIDModalVisibility(false)} 
				onSubmit={updateCallerDid} 
				callerDid={selectedRows[0]} 
			/>}

			{openDeactivateAction && <ActionConfirmationModal
				visible={openDeactivateAction}
				onClose={() => setDeactivateActionVisibility(false)}
				onConfirm={deactivateCallerDids}
				headerText={"Warning!"}
				bodyText={"This would deactivate selected caller dids."}
			/>}

			{openCallerDIDModal && <AddCallerDIDModal
				open={openCallerDIDModal} 
				onClose={() => setAddCallerDIDModalVisibility(false)} 
				onSubmit={addNewCallerDIDs}
			/>}

		</Box>
	);
};

export default CallerDIDConfig;